import { JSX } from "react";
import { type MRT_Row as Row } from "material-react-table";
import { enumKeys, enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import { isPermissionsRow, isTemplateRow } from "permissions/_views/tables/_utilities/utils";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { Resource } from "permissions/resources/permission-resource";
import { TableRow, GroupedCellConfig, CardType, MetaControlType } from "permissions/_views/types/permission-types";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/types";
import { ResourceConstructor, AccessLevel } from "permissions/resources/types";

type TopLevelTemplates = {
  byConstructor: Map<ResourceConstructor, Resource>;
  legalByAccessType: Map<string, Resource>;
  docContentsBySubject: Map<string, Resource>;
};

type LegalEntityGroupCellProps = {
  row: TableRow | null;
  topLevelTemplates: TopLevelTemplates;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  canEdit: boolean;
  creatingRow: boolean | null;
  editingRow: Row<TableRow> | null;
  resources: Resource[];
  renderGroupedPermission: (row: TableRow, config: GroupedCellConfig) => JSX.Element;
};

export default function LegalEntityGroupCell(props: Readonly<LegalEntityGroupCellProps>): JSX.Element | null {
  const {
    row,
    topLevelTemplates,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    canEdit,
    creatingRow,
    editingRow,
    resources,
    renderGroupedPermission,
  } = props;

  if (!row) return null;
  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(LegalEntityAccessType).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    templateForKey: (atKey) => topLevelTemplates.legalByAccessType.get(atKey),
    candidatesForKey: (row, atKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter(
          (permission) =>
            permission.resource instanceof LegalEntityAccess &&
            enumKeyOf(LegalEntityAccessType, permission.resource.accessType) === atKey
        );
      }
      if (isTemplateRow(row)) {
        let source: Resource[];
        const isCreatingThisRow = creatingRow && row.id?.isEqualTo?.(Guid.EMPTY);
        const isEditingThisRow = Boolean(editingRow?.original) && editingRow!.original.id?.isEqualTo?.(row.id);

        if (isEditingThisRow || isCreatingThisRow) {
          source = resources;
        } else {
          source = row.resources;
        }

        return source.filter(
          (resource) =>
            resource instanceof LegalEntityAccess && enumKeyOf(LegalEntityAccessType, resource.accessType) === atKey
        );
      }
      return [];
    },
    labelForKey: (atKey) => atKey,
    controlMetaForResource: (resource) => ({
      type: MetaControlType.AccessLevel,
      label: "Access Level",
      options: enumKeys(AccessLevel),
      value: enumKeyOf(AccessLevel, (resource as LegalEntityAccess).accessLevel),
    }),
    title: isSingleGranteeView || isCreateTemplateView || isManageTemplatesView ? "Legal Entity" : undefined,
    colSpan: 2,
    rowSpan: 2,
    canEdit,
  });
}
